import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import apiService from '../services/api';
import { REALTIME_WS_URL } from '../config/environment';
import { queryClient } from '../query/queryClient';
import { queryKeyForTopic } from '../query/keys';
import { startDomainSocket } from '../realtime/domainSocket';
import { takeFreshEvents } from '../realtime/events';
import { useAuth } from './AuthContext';

const POLL_MS = 2500;
const SCREEN_SYNC_MS = 3000;

export const DOMAIN_TOPICS = {
  packages: 'packages',
  invoices: 'invoices',
  payments: 'payments',
  cobrancas: 'cobrancas',
  clients: 'clients',
  coletas: 'coletas',
  products: 'products',
  documents: 'documents',
  users: 'users',
  suppliers: 'suppliers',
  catalog: 'catalog',
  tracking: 'tracking',
  account_deletions: 'account_deletions',
};

const RealtimeContext = createContext({
  subscribe: (_topics, _onEvent) => () => undefined,
  connected: false,
  transport: 'offline',
});

export const useRealtime = () => useContext(RealtimeContext);

export const RealtimeProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const listenersRef = useRef(new Set());
    const sinceRef = useRef(0);
    const primedRef = useRef(false);
  const seenRef = useRef(new Set());
  const transportRef = useRef('offline');
  const [connected, setConnected] = useState(false);
  const [transport, setTransport] = useState('offline');

  const setLiveTransport = useCallback((next) => {
    transportRef.current = next;
    setTransport(next);
  }, []);

  const subscribe = useCallback((topics, onEvent) => {
    const topicSet = new Set(
      (Array.isArray(topics) ? topics : [topics]).filter(Boolean)
    );
    const listener = { topicSet, onEvent };
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const dispatch = useCallback((events) => {
    const fresh = takeFreshEvents(seenRef.current, events);
    seenRef.current = fresh.seen;
    if (fresh.events.length === 0) return;

    const topics = new Set();
    for (const event of fresh.events) {
      const at = Number(event?.at) || 0;
      if (at > sinceRef.current) sinceRef.current = at;
      const topic = event?.topic || String(event?.event || '').split(':')[0];
      if (topic) topics.add(topic);
      listenersRef.current.forEach((listener) => {
        if (listener.topicSet.size === 0 || listener.topicSet.has(topic)) {
          try {
            listener.onEvent(event);
          } catch (err) {
            console.error('Realtime listener error', err);
          }
        }
      });
    }

    topics.forEach((topic) => {
      queryClient.invalidateQueries({ queryKey: queryKeyForTopic(topic) });
    });
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setConnected(false);
      setLiveTransport('offline');
      return undefined;
    }

    let cancelled = false;
    let pollId = null;
    let socket = null;
    primedRef.current = false;
    sinceRef.current = 0;

    const tick = async () => {
      if (cancelled || document.hidden) return;
      try {
        const data = await apiService.getDomainRealtimeStream({
          since: primedRef.current ? sinceRef.current : 0,
        });
        if (cancelled) return;
        setConnected(true);
        if (transportRef.current !== 'websocket') {
          setLiveTransport('http');
        }
        if (!primedRef.current) {
          primedRef.current = true;
          if (data?.serverTime) sinceRef.current = Number(data.serverTime);
          return;
        }
        if (Array.isArray(data?.events) && data.events.length) {
          dispatch(data.events);
        }
      } catch {
        if (!cancelled) {
          setConnected(false);
          if (transportRef.current !== 'websocket') {
            setLiveTransport('offline');
          }
        }
      }
    };

    const pingScreens = () => {
      if (cancelled || document.hidden) return;
      listenersRef.current.forEach((listener) => {
        try {
          listener.onEvent({ id: `sync-${Date.now()}`, topic: 'sync', at: 0 });
        } catch (err) {
          console.error('Realtime listener error', err);
        }
      });
      queryClient.invalidateQueries({ queryKey: ['domain'] });
    };

    const startPoll = () => {
      if (pollId) return;
      tick();
      pollId = window.setInterval(tick, POLL_MS);
    };

    const stopPoll = () => {
      if (pollId) window.clearInterval(pollId);
      pollId = null;
    };

    startPoll();
    const syncId = window.setInterval(pingScreens, SCREEN_SYNC_MS);

    if (REALTIME_WS_URL) {
      socket = startDomainSocket({
        url: REALTIME_WS_URL,
        getToken: () => localStorage.getItem('token') || '',
        getSince: () => sinceRef.current,
        onEvent: (event) => dispatch([event]),
        onOpen: () => {
          if (cancelled) return;
          setConnected(true);
          setLiveTransport('websocket');
          stopPoll();
        },
        onClose: () => {
          if (cancelled) return;
          setConnected(false);
          setLiveTransport('http');
          startPoll();
        },
      });
    }

    const onVisible = () => {
      if (document.hidden) return;
      if (transportRef.current !== 'websocket') tick();
      pingScreens();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      stopPoll();
      window.clearInterval(syncId);
      socket?.close();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [dispatch, isAuthenticated, setLiveTransport]);

  const value = useMemo(
    () => ({
      subscribe,
      connected,
      transport,
    }),
    [subscribe, connected, transport]
  );

  return (
    <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>
  );
};
